// ── GA4 Analytics Utility ─────────────────────────────────────────────────────
// Wraps window.gtag so calls are safe even before GA4 loads.

type GTagEvent = {
  event_category?: string;
  event_label?: string;
  value?: number;
  [key: string]: string | number | boolean | undefined;
};

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function gtag(command: 'event', eventName: string, params?: GTagEvent): void {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag(command, eventName, params);
  }
}

// ── Visitor Tracking ──────────────────────────────────────────────────────────
export const trackHomepageVisit = () =>
  gtag('event', 'page_view', { event_category: 'visitor', event_label: 'homepage' });

export const trackAskPageVisit = () =>
  gtag('event', 'page_view', { event_category: 'visitor', event_label: 'ask_hpe_page' });

// ── Engagement Tracking ───────────────────────────────────────────────────────
export const trackClickAskFree = () =>
  gtag('event', 'click_ask_hpe_free', { event_category: 'engagement', event_label: 'ask_2_questions_free' });

export const trackFirstQuestionSubmitted = () =>
  gtag('event', 'first_question_submitted', { event_category: 'engagement', event_label: 'question_1' });

export const trackSecondQuestionSubmitted = () =>
  gtag('event', 'second_question_submitted', { event_category: 'engagement', event_label: 'question_2' });

// ── Conversion Tracking ───────────────────────────────────────────────────────
export const trackClickEnterProAccess = () =>
  gtag('event', 'click_enter_pro_access', { event_category: 'conversion', event_label: 'pro_access_cta' });

export const trackCheckoutStarted = (plan?: string) =>
  gtag('event', 'checkout_started', { event_category: 'conversion', event_label: plan ?? 'unknown' });

export const trackCheckoutCompleted = (plan?: string) =>
  gtag('event', 'checkout_completed', { event_category: 'conversion', event_label: plan ?? 'unknown' });

// ── Subscription Tracking ─────────────────────────────────────────────────────
export const trackWeeklyPlanPurchase = () =>
  gtag('event', 'purchase', { event_category: 'subscription', event_label: 'weekly_17', value: 17 });

export const trackMonthlyPlanPurchase = () =>
  gtag('event', 'purchase', { event_category: 'subscription', event_label: 'monthly_47', value: 47 });

// ── Answer Feedback ───────────────────────────────────────────────────────────
export const trackAnswerFeedback = (helpful: boolean) =>
  gtag('event', 'answer_feedback', { event_category: 'feedback', event_label: helpful ? 'helpful' : 'not_quite', value: helpful ? 1 : 0 });

// ── Challenge HPE A/B Tracking ────────────────────────────────────────────────
export const trackChallengeHPEClick = () =>
  gtag('event', 'ab_card_click', { event_category: 'can_you_beat_hpe', event_label: 'challenge_hpe' });

export const trackOutsmartAIClick = () =>
  gtag('event', 'ab_card_click', { event_category: 'can_you_beat_hpe', event_label: 'outsmart_ai' });

export const trackChallengeSignup = (variant: string) =>
  gtag('event', 'challenge_signup', { event_category: 'can_you_beat_hpe', event_label: variant });

export const trackChallengeSubmission = (variant: string) =>
  gtag('event', 'challenge_submission_completed', { event_category: 'can_you_beat_hpe', event_label: variant });

// ── Competition Funnel Tracking ───────────────────────────────────────────────
// These events track distinct steps in the competition funnel.
// Page visits are tracked separately and are NOT counted as entries.

/** Fired once when the Outsmart the AI page mounts */
export const trackCompetitionPageViewed = () =>
  gtag('event', 'competition_page_viewed', { event_category: 'competition_funnel', event_label: 'fix_it_challenge' });

/** Fired when any "Enter" button is clicked (category cards or the CTA) */
export const trackCompetitionEnterClicked = (category?: string) =>
  gtag('event', 'competition_enter_clicked', { event_category: 'competition_funnel', event_label: category ?? 'all' });

/** Fired when an unauthenticated user clicks Enter and the signup/modal opens */
export const trackCompetitionSignupClicked = () =>
  gtag('event', 'competition_signup_clicked', { event_category: 'competition_funnel', event_label: 'auth_modal_opened' });

/** Fired when a user starts a paid subscription from the competition flow */
export const trackCompetitionSubscriptionStarted = (plan?: string) =>
  gtag('event', 'competition_subscription_started', { event_category: 'competition_funnel', event_label: plan ?? 'unknown' });

/** Fired when the entry form is successfully opened (user is authenticated) */
export const trackCompetitionFormOpened = (category?: string) =>
  gtag('event', 'competition_form_opened', { event_category: 'competition_funnel', event_label: category ?? 'all' });

/** Fired only after a successful DB insert — the true "entry submitted" conversion */
export const trackCompetitionEntrySubmitted = (category?: string) =>
  gtag('event', 'competition_entry_submitted', { event_category: 'competition_funnel', event_label: category ?? 'all' });
